import type { ServiceDetail } from "./types";

export const SEO_SEM: ServiceDetail = {
  slug: "seo-sem",
  category: "digital",
  name: "SEO & SEM",
  hero: {
    eyebrow: "SEO & SEM",
    title: "Traffico qualificato e ROI misurabile, non solo visite.",
    subtitle:
      "Strategia combinata SEO + SEM: il SEO costruisce posizionamenti organici durevoli nel tempo, le campagne Google Ads (SEM) puntano a generare richieste qualificate fin dalle prime settimane. Il mix giusto per ogni fase di crescita, misurato sul costo per lead reale. Sede a Roma, certificati ISO 9001:2015.",
    bullets: [
      "Audit tecnico SEO e piano editoriale a 12 mesi",
      "Campagne Google Ads Search, Performance Max e retargeting",
      "Tracciamento conversioni server-side e dashboard Looker Studio",
      "Report mensile con ranking, costo per lead e prossime azioni",
    ],
  },
  problem: [
    "Il sito di tante PMI è di fatto invisibile su Google: pochi contenuti, struttura tecnica fragile (Core Web Vitals fuori soglia, pagine non indicizzate, schema mancante) e nessun piano editoriale. Il founder pensa che la SEO sia roba lenta e cara, e così non parte mai.",
    "In parallelo, le campagne Google Ads vengono lasciate a chi le ha attivate anni fa: parole chiave generiche, nessun tracciamento conversioni serio, account ads disordinato. Il budget viene bruciato in click che non diventano richieste, e le vere parole chiave commerciali restano scoperte.",
    "Il risultato è una dipendenza pericolosa da un solo canale (di solito il passaparola) e una reportistica opaca: il founder non sa quante richieste arrivano davvero da Google, quanto costano, su quali parole chiave i concorrenti stanno guadagnando posizioni mese dopo mese.",
  ],
  approach: [
    "Partiamo da un technical audit completo: Lighthouse, Core Web Vitals, Search Console, indicizzazione, struttura URL, schema.org, internal linking. Output: lista priorità con quick win nei primi 30 giorni e roadmap a 90 giorni.",
    "Facciamo keyword research commerciale, non vanity: identifichiamo le query con intent transazionale per il tuo settore, mappate al funnel (awareness, consideration, decisione) e prioritizzate per volume realistico × difficoltà × valore commerciale.",
    "Costruiamo content calendar e ottimizzazione on-page: pagine pillar sui servizi principali, cluster di approfondimento, blog editoriale e schema markup. Ogni contenuto ha un obiettivo di posizionamento dichiarato e un KPI di lead atteso.",
    "In parallelo gestiamo le campagne SEM (Google Ads Search, Performance Max e retargeting) con tracciamento conversioni server-side e A/B test continui. Mentre la SEO matura nei mesi, il SEM porta richieste qualificate da subito.",
  ],
  deliverables: [
    {
      title: "Audit tecnico Lighthouse + Search Console",
      detail:
        "Analisi completa di performance, Core Web Vitals, indicizzazione e schema. Output: gap report con priorità e quick win 30/60/90 giorni.",
    },
    {
      title: "Piano keyword a 12 mesi",
      detail:
        "Mappatura keyword commerciali per intent (informazionale, navigazionale, transazionale), volume, difficoltà e valore atteso per cluster.",
    },
    {
      title: "Calendario contenuti pillar + cluster",
      detail:
        "Editoriale a 6 mesi: pagine pillar sui servizi principali, articoli cluster di approfondimento e schema markup per ogni contenuto.",
    },
    {
      title: "Audit tecnico Google Ads",
      detail:
        "Analisi dell'account Google Ads — struttura campagne, keyword, quality score, tracking conversioni e sprechi di budget — con report e azioni prioritarie. Puoi richiederlo come intervento a sé su /audit-google-ads.",
    },
    {
      title: "Setup Google Ads + tracking conversioni",
      detail:
        "Riorganizzazione account Google Ads, campagne Search + Performance Max + retargeting, tracking server-side e attribuzione fino al CRM.",
    },
    {
      title: "Dashboard Looker Studio",
      detail:
        "Dashboard centralizzata e sempre aggiornata con ranking SEO, traffico organico, CPL Google Ads, ROI per campagna e benchmark vs concorrenti.",
    },
    {
      title: "Report mensile e board strategico",
      detail:
        "Report di 1 pagina con ranking, costo per lead, evoluzione organica e azioni del mese successivo. Board mensile di 30 minuti con il founder.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Settimana 1-2 — Audit & keyword research",
      text: "AB MARK conduce technical audit, analisi competitor e keyword research commerciale. Tu fornisci accesso a Search Console, GA4, Google Ads e CRM. Output condiviso: gap report e piano editoriale a 12 mesi.",
    },
    {
      step: "02",
      title: "Mese 1 — Quick win",
      text: "AB MARK sistema gli errori tecnici a maggior impatto, riorganizza l'account Google Ads e lancia le prime campagne Search. Tu approvi messaggi commerciali e priorità delle pagine da ottimizzare per prime.",
    },
    {
      step: "03",
      title: "Mese 3-6 — Scaling",
      text: "AB MARK pubblica contenuti pillar e cluster, scala le campagne SEM che convertono, lavora link building e ottimizzazione on-page. Tu condividi feedback dal team commerciale sulla qualità reale dei lead arrivati.",
    },
    {
      step: "04",
      title: "Ongoing — Ottimizzazione continua",
      text: "AB MARK gestisce produzione contenuti, A/B test SEM, monitoraggio ranking e link building. Tu partecipi al board mensile di 30 minuti per leggere i KPI e validare le decisioni del mese successivo.",
    },
  ],
  faq: [
    {
      q: "Quanto tempo serve per vedere risultati SEO concreti?",
      a: "Dipende molto dal settore e dalla concorrenza, ma in genere i primi miglioramenti misurabili (impression e click su keyword di media coda) arrivano in 3-4 mesi. Per posizionamenti su keyword competitive servono 9-12 mesi di lavoro continuo. Per questo abbiniamo sempre il SEM, che può portare richieste qualificate fin dal primo mese di attivazione.",
    },
    {
      q: "Qual è il budget minimo consigliato per Google Ads?",
      a: "Consigliamo un minimo di 1.000-1.500 € al mese di budget media (escluso il fee della nostra agenzia) per generare dati statisticamente significativi e poter ottimizzare entro 60 giorni. Per settori molto competitivi (legale, finanziario, immobiliare, sanitario) la soglia minima utile sale a 3.000-5.000 € al mese.",
    },
    {
      q: "Come vi differenziate da un'agenzia di marketing generalista?",
      a: "Non siamo specialisti SEO che ignorano il SEM, né performance marketer che trascurano l'organico: integriamo le due discipline in una strategia unica, con la stessa keyword research a guidare contenuti SEO e campagne Google Ads. Inoltre, rendicontiamo sempre fino al lead in CRM, non ci fermiamo al click.",
    },
    {
      q: "Fate un audit dell'account Google Ads esistente?",
      a: "Sì. Se hai già un account Google Ads attivo, puoi partire da un audit tecnico dedicato che analizza struttura delle campagne, keyword, quality score, tracking delle conversioni e sprechi di budget, restituendoti un report con le azioni prioritarie. Trovi il dettaglio e il modulo di richiesta su /audit-google-ads. L'audit può essere gratuito o a pagamento a seconda della complessità dell'account, e non promettiamo risultati a priori.",
    },
    {
      q: "Su quali settori siete più esperti?",
      a: "I nostri settori principali sono B2B servizi professionali (consulenza, IT, ingegneria), industria e manifattura italiana, immobiliare commerciale, food & wine ed e-commerce di nicchia. Lavoriamo prevalentemente con PMI italiane fra 5 e 200 dipendenti, anche con esigenze multilingua.",
    },
    {
      q: "Quanto dura il contratto di gestione SEO/SEM?",
      a: "I contratti SEO sono semestrali rinnovabili (3 mesi minimi servono solo per recuperare l'investimento iniziale di audit e impostazione). Quelli SEM sono mensili senza vincolo. Se dopo 6 mesi non vedi una pipeline più sana, non hai alcun obbligo di rinnovo.",
    },
    {
      q: "Come dividete il budget fra SEO e SEM?",
      a: "Per chi parte da zero consigliamo nei primi 3-6 mesi un mix orientato al SEM (60-70% del budget) per portare richieste subito, mentre la SEO costruisce la base. Dal mese 6-12 il rapporto tende a invertirsi (60-70% sulla SEO consolidata) perché il costo per lead organico tende ad essere più basso nel lungo periodo.",
    },
  ],
  seo: {
    title: "Agenzia SEO Roma e SEM Google Ads per PMI B2B | AB MARK",
    description:
      "Agenzia SEO Roma e SEM Google Ads per PMI italiane: consulenza SEO B2B, audit tecnico, piano keyword 12 mesi, campagne Google Ads e dashboard ROI mensile.",
  },
};
